import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Plus, Loader2, Settings, Users } from 'lucide-react';
import { NOVA_DM_ID } from '../../assistant/novaTurn';
import { aiAgentPortraitSrc, getAiAgentSettings, setAiAgentEnabled } from '../../../services/cabinet/aiAgentSettings';
import { useChannels, type Channel } from '../../../hooks/queries/useMessaging';
import { useSidecarStore } from '../../../store/useSidecarStore';
import { SidecarHeaderActions } from './SidecarHeaderActions';
import { usePresence } from '../../../hooks/usePresence';
import { useAuthStore } from '../../../store/useAuthStore';
import { canAccessFeature, asCollaboratorRole } from '../../../services/auth/permissions';
import { PresencePanel } from './PresencePanel';
import { NewDmModal } from '../NewDmModal';
import { ChannelSettingsModal } from '../ChannelSettingsModal';
import { ChannelMembersModal } from '../ChannelMembersModal';
import { cn } from '../../../lib/cn';

function ChannelRow({
  channel,
  onSelect,
  canManage,
  onEdit,
  onMembers,
  isActive,
}: {
  channel: Channel;
  onSelect: () => void;
  canManage: boolean;
  onEdit: (c: Channel) => void;
  onMembers: (c: Channel) => void;
  isActive?: boolean;
}) {
  const hasUnread = (channel.unreadCount ?? 0) > 0;
  const isDm = channel.kind === 'dm';
  const displayName = isDm
    ? channel.dmOtherCollaborator?.full_name ?? 'Message direct'
    : channel.name ?? 'canal';

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-left transition',
          isActive
            ? 'bg-cime-primary/10 text-cime-text-primary'
            : 'text-cime-text-muted hover:bg-cime-primary/5 hover:text-cime-text-primary',
        )}
      >
        {isDm ? (
          channel.dmOtherCollaborator?.avatar_url ? (
            <img
              src={channel.dmOtherCollaborator.avatar_url}
              alt=""
              className="w-4 h-4 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <MessageCircle className="w-4 h-4 flex-shrink-0" />
          )
        ) : (
          <img
            src={`/icons/${channel.isPrivate ? 'lock-hashtag' : (channel.emoji ?? 'hashtag')}.svg`}
            alt=""
            className="w-4 h-4 flex-shrink-0 opacity-60"
          />
        )}
        <span className={cn('flex-1 truncate', hasUnread && 'font-semibold text-cime-text-primary')}>
          {displayName}
        </span>
        {hasUnread && (
          <span className="min-w-5 h-5 flex items-center justify-center rounded-full bg-cime-gold text-cime-primary text-xs font-semibold px-1 flex-shrink-0">
            {(channel.unreadCount ?? 0) > 99 ? '99+' : channel.unreadCount}
          </span>
        )}
      </button>

      {canManage && !isDm && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-0.5 bg-white/80 rounded">
          {channel.isPrivate && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onMembers(channel); }}
              className="p-1 rounded hover:bg-cime-primary/10 text-cime-text-muted hover:text-cime-primary transition-colors"
              title="Gérer les membres"
            >
              <Users className="w-3 h-3" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(channel); }}
            className="p-1 rounded hover:bg-cime-primary/10 text-cime-text-muted hover:text-cime-primary transition-colors"
            title="Paramètres du canal"
          >
            <Settings className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

export function SidecarPanelList({
  selectedChannelId = null,
  onSelectChannel,
  hideWindowActions = false,
}: {
  selectedChannelId?: string | null;
  onSelectChannel?: (channelId: string) => void;
  hideWindowActions?: boolean;
} = {}) {
  const storeSelect = useSidecarStore((s) => s.selectChannel);
  const selectChannel = onSelectChannel ?? storeSelect;
  const { data: channels = [], isLoading } = useChannels();
  const presence = usePresence();
  const collaborator = useAuthStore((s) => s.collaborator);
  const role = asCollaboratorRole(collaborator?.role);
  const canCreate = canAccessFeature(role, 'messaging_create_channel');
  const canManage = canAccessFeature(role, 'messaging_manage_channel');
  const canToggleAgent = canAccessFeature(role, 'settings_ai_agent');
  const queryClient = useQueryClient();

  const [showNewDm, setShowNewDm] = useState(false);
  const [settingsChannel, setSettingsChannel] = useState<Channel | null>(null);
  const [membersChannel, setMembersChannel] = useState<Channel | null>(null);

  const globals = channels.filter((c) => c.kind === 'global');
  const dms = channels.filter((c) => c.kind === 'dm');
  const agent = useQuery({
    queryKey: ['cabinet', 'ai-agent'],
    queryFn: getAiAgentSettings,
    staleTime: 60_000,
  });
  const agentEnabled = agent.data?.enabled === true;
  const agentName = agent.data?.name || 'Nova Stellaris';
  const agentSrc = aiAgentPortraitSrc(agent.data?.photoUrl ?? null);
  const toggleAgent = useMutation({
    mutationFn: (enabled: boolean) => setAiAgentEnabled(enabled, collaborator?.id ?? null),
    onSuccess: (next) => {
      queryClient.setQueryData(['cabinet', 'ai-agent'], next);
      if (!next.enabled && selectedChannelId === NOVA_DM_ID) selectChannel('');
    },
  });

  return (
    <>
      <header className="flex items-center justify-between px-3 py-2 border-b border-neutral-200 flex-shrink-0">
        <h2 className="text-sm font-semibold text-cime-text-primary">Messagerie</h2>
        {hideWindowActions ? null : <SidecarHeaderActions />}
      </header>

      <PresencePanel presence={presence} />

      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-6 text-cime-text-muted">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : (
          <>
            <section>
              <div className="flex items-center justify-between px-2 mb-1">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-cime-text-muted">
                  Canaux
                </h3>
                {canCreate && (
                  <button
                    type="button"
                    onClick={() => setSettingsChannel({
                      id: '',
                      name: null,
                      kind: 'global',
                      isPrivate: false,
                      createdBy: '',
                      createdAt: '',
                      description: null,
                      emoji: 'hashtag',
                      archivedAt: null,
                      dossierId: null,
                    } as Channel)}
                    className="p-0.5 rounded hover:bg-cime-primary/10 text-cime-text-muted hover:text-cime-primary transition-colors"
                    title="Créer un canal"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="space-y-0.5">
                {globals.length === 0 ? (
                  <p className="px-3 py-1 text-xs text-cime-text-muted italic">Aucun canal</p>
                ) : (
                  globals.map((ch) => (
                    <ChannelRow
                      key={ch.id}
                      channel={ch}
                      onSelect={() => selectChannel(ch.id)}
                      canManage={canManage}
                      onEdit={setSettingsChannel}
                      onMembers={setMembersChannel}
                      isActive={ch.id === selectedChannelId}
                    />
                  ))
                )}
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between px-2 mb-1">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-cime-text-muted">
                  Messages directs
                </h3>
                <div className="flex items-center gap-1">
                {canToggleAgent && (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={agentEnabled}
                    aria-label="Assistant IA"
                    title={agentEnabled ? 'Désactiver l’assistant IA' : 'Activer l’assistant IA'}
                    disabled={toggleAgent.isPending}
                    onClick={() => toggleAgent.mutate(!agentEnabled)}
                    className={cn(
                      'relative h-4 w-7 shrink-0 rounded-full transition-colors',
                      agentEnabled ? 'bg-cime-primary' : 'bg-neutral-300',
                    )}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 h-3 w-3 rounded-full bg-white transition-[left]',
                        agentEnabled ? 'left-3.5' : 'left-0.5',
                      )}
                    />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowNewDm(true)}
                  className="p-0.5 rounded hover:bg-cime-primary/10 text-cime-text-muted hover:text-cime-primary transition-colors"
                  title="Nouveau message direct"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
                </div>
              </div>
              <div className="space-y-0.5">
                {agentEnabled && (
                  <button
                    type="button"
                    onClick={() => selectChannel(NOVA_DM_ID)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-left transition',
                      selectedChannelId === NOVA_DM_ID
                        ? 'bg-cime-primary/10 text-cime-text-primary'
                        : 'text-cime-text-muted hover:bg-cime-primary/5 hover:text-cime-text-primary',
                    )}
                  >
                    <img src={agentSrc} alt="" className="h-6 w-6 rounded-full object-cover shrink-0" />
                    <span className="truncate font-medium">{agentName}</span>
                  </button>
                )}
                {dms.length === 0 ? (
                  <p className="px-3 py-1 text-xs text-cime-text-muted italic">
                    {agentEnabled ? 'Aucun autre message direct' : 'Aucun message direct'}
                  </p>
                ) : (
                  dms.map((ch) => (
                    <ChannelRow
                      key={ch.id}
                      channel={ch}
                      onSelect={() => selectChannel(ch.id)}
                      canManage={canManage}
                      onEdit={setSettingsChannel}
                      onMembers={setMembersChannel}
                      isActive={ch.id === selectedChannelId}
                    />
                  ))
                )}
              </div>
            </section>
          </>
        )}
      </div>

      {showNewDm && (
        <NewDmModal
          onClose={() => setShowNewDm(false)}
          onCreated={(id) => { setShowNewDm(false); selectChannel(id); }}
        />
      )}

      {settingsChannel && (
        <ChannelSettingsModal
          mode={settingsChannel.id ? 'edit' : 'create'}
          channel={settingsChannel.id ? settingsChannel : undefined}
          onClose={() => setSettingsChannel(null)}
          onCreated={(id) => { setSettingsChannel(null); selectChannel(id); }}
          onUpdated={() => setSettingsChannel(null)}
          onDeleted={() => setSettingsChannel(null)}
        />
      )}

      {membersChannel && (
        <ChannelMembersModal
          channel={membersChannel}
          onClose={() => setMembersChannel(null)}
        />
      )}
    </>
  );
}
